"use client"

import { AppDownloadCTA } from "@/components/ui/app-download-cta"

/**
 * Blog mid-content app CTA, mounted via the inline-tool-widget marker system
 * (data-tool="app-download"). Injected into every published post except
 * weed/herbicide/product-application intent, which content-processor suppresses;
 * `resolveAppDownloadVariant` in lib/content-processor.ts picks the
 * variant from the slug so the copy names the reader's current job instead of
 * using a generic app pitch. Self-gates through AppDownloadCTA, which also
 * suppresses the whole card on Android (iOS-only app, the store link dead-ends).
 */
const COPY = {
  "grass-id": {
    headline: "Skip the checklist. Scan your grass instead.",
    subhead:
      "The free iPhone app identifies your grass from one photo and builds a care plan for your ZIP.",
  },
  "weed-check": {
    headline: "Check the lawn problem before you spray",
    subhead:
      "Photograph the weed or damage in the free iPhone app. Get an identification and a lawn-safe next step.",
  },
  // Disease/pest posts. Largest uncovered block on the blog (2,046
  // pageview-sessions/60d across the *-diseases-identification-treatment
  // cluster). Their reader's problem is "my lawn is sick", which the grass-ID
  // pitch does not name, so it gets the diagnose framing instead.
  "lawn-problem": {
    headline: "Find out what is wrong before you treat it",
    subhead:
      "Photograph the damage in the free iPhone app. Get a diagnosis and a treatment plan matched to your grass and your ZIP.",
  },
  // Fallback for every post the slug rules do not classify. Names what the app
  // does rather than pitching "download our app", which is the generic ask the
  // intent-matched variants exist to avoid.
  general: {
    headline: "Your lawn's care plan, on your phone",
    subhead:
      "The free iPhone app identifies your grass from one photo and builds a month-by-month plan for your ZIP.",
  },
} as const

export function InlineAppDownload({
  slug,
  variant = "grass-id",
}: {
  variant?: string
  slug?: string
  tags?: string[]
}) {
  const copy = COPY[variant as keyof typeof COPY] ?? COPY["grass-id"]
  return (
    <AppDownloadCTA
      placement={`blog_mid_${slug ?? "unknown"}`}
      variant="card"
      className="my-8"
      headline={copy.headline}
      subhead={copy.subhead}
    />
  )
}

export default InlineAppDownload
